/**
 * I path della sezione Limulo, in un posto solo — e sotto un prefisso, sempre.
 *
 * ⚠️ PERCHE' NON BASTA «metterle in fondo al router». `createBrowserRouter` (React Router
 * v7) risolve per RANK di specificita', non per ordine di dichiarazione: la posizione
 * nell'array non protegge da niente. E davanti a un path DUPLICATO ESATTO vince chi e'
 * dichiarato prima, **senza errori, senza warning, con la build verde**.
 *
 * Su `main` esistono gia' `tournaments`, `tournaments/:id`, `tournaments/:id/watch-party`
 * e `teams/:id`: sono la superficie tornei del monolite Laravel, viva in produzione. Una
 * rotta nuda di questa sezione le metterebbe in ombra — e siccome le nostre stanno dietro
 * `RoleGate role="beta"`, l'ombra la vedrebbero SOLO gli utenti beta. E' la classe di bug
 * peggiore che si possa costruire qui: rotto per pochi, invisibile a chi lo cerca.
 *
 * La convenzione del repo (chess, duels, chinesepoker la seguono gia') e' il prefisso di
 * sezione. Tenerla qui, in una lista che `tools/verify-limulo.ts` controlla, la rende
 * meccanica invece che ricordata.
 */
export const LIMULO_ROUTE_PREFIX = 'limulo';

export const LIMULO_ROUTE_PATHS = [
  `${LIMULO_ROUTE_PREFIX}/tournaments`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/standings`,
  `${LIMULO_ROUTE_PREFIX}/mine`,
  `${LIMULO_ROUTE_PREFIX}/diagnostics`,
  // ⚠️ In CODA, e non per pigrizia: `LIMULO_PATHS` qui sotto indicizza questo array per
  // POSIZIONE. Inserire in mezzo sposta ogni indice successivo, e il risultato non e' un
  // errore di compilazione — e' una rotta che punta al componente sbagliato, con la build
  // verde. Chi aggiunge, aggiunge in fondo.
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/matches`,
  `${LIMULO_ROUTE_PREFIX}/tournaments/:id/matches/:matchId`,
  `${LIMULO_ROUTE_PREFIX}/profiles`,
  `${LIMULO_ROUTE_PREFIX}/profiles/new/:gameId`,
  // ⚠️ IN CODA, come dice la nota qui sopra: `LIMULO_PATHS` indicizza per POSIZIONE.
  `${LIMULO_ROUTE_PREFIX}/history`,
] as const;

export const LIMULO_PATHS = {
  tournaments: LIMULO_ROUTE_PATHS[0],
  tournamentDetail: LIMULO_ROUTE_PATHS[1],
  standings: LIMULO_ROUTE_PATHS[2],
  mine: LIMULO_ROUTE_PATHS[3],
  diagnostics: LIMULO_ROUTE_PATHS[4],
  matches: LIMULO_ROUTE_PATHS[5],
  matchDetail: LIMULO_ROUTE_PATHS[6],
  profiles: LIMULO_ROUTE_PATHS[7],
  profileCreate: LIMULO_ROUTE_PATHS[8],
  history: LIMULO_ROUTE_PATHS[9],
} as const;

/**
 * Gli URL di navigazione si costruiscono QUI e non nelle schermate: un `to="/limulo/…"`
 * scritto a mano in un componente e' una copia del path che non si aggiorna quando il
 * path cambia, e un link morto dentro una sezione dietro flag e' l'ultima cosa che
 * qualcuno noterebbe.
 */
export const limuloTournamentUrl = (id: string): string =>
  `/${LIMULO_ROUTE_PREFIX}/tournaments/${encodeURIComponent(id)}`;

export const limuloStandingsUrl = (id: string): string =>
  `${limuloTournamentUrl(id)}/standings`;

export const LIMULO_MINE_URL = `/${LIMULO_ROUTE_PREFIX}/mine`;

/**
 * Lo storico dei tornei conclusi (A3).
 *
 * ⚠️ **Non e' sotto `/tournaments`**: quella e' la vetrina dei tornei pubblicati, e lo storico
 * e' una cosa dell'UTENTE — sul filo e' `GET /v1/me/tournaments/history`, dove l'identita' la
 * ricava il server dal contesto. Metterlo sotto la vetrina farebbe credere che si possa
 * chiedere lo storico di un torneo, o di un altro.
 */
export const LIMULO_HISTORY_URL = `/${LIMULO_ROUTE_PREFIX}/history`;

/**
 * L'ingresso alla sezione, per chi ci arriva da FUORI (oggi: la scheda Tornei).
 *
 * Sta qui e non nella schermata chiamante per la stessa ragione delle altre: un
 * `to="/limulo/…"` scritto a mano e' un path duplicato che nessun controllo lega a
 * `LIMULO_ROUTE_PATHS`, e il giorno che il prefisso cambia resta indietro in silenzio.
 */
export const LIMULO_TOURNAMENTS_URL = `/${LIMULO_ROUTE_PREFIX}/tournaments`;

/**
 * Il semaforo della catena d'identita'.
 *
 * ⚠️ ESISTE PERCHE' LA ROTTA ERA VIVA E IRRAGGIUNGIBILE. Fino al 2026-09-11 `diagnostics` era
 * l'UNICA delle nove rotte di questa sezione senza un URL di navigazione qui dentro, e il suo
 * path compariva in un solo punto di tutto `src/`: la dichiarazione della rotta stessa
 * (`routes/router.tsx`). Misurato, con il controllo positivo accanto — lo stesso conteggio
 * dava >= 1 per tutte le altre otto. ⇒ ci si arrivava solo digitando l'URL a mano, che dentro
 * una WebView di Telegram vuol dire: non ci si arrivava.
 *
 * E' la stessa lezione di #116 gia' scritta due volte in questo file — «una rotta senza una
 * via per arrivarci e' una rotta morta» — ripetuta una terza volta perche' ripeterla non era
 * bastato: ora la tiene un cancello in `tools/verify-limulo.ts`, non un commento.
 */
export const LIMULO_DIAGNOSTICS_URL = `/${LIMULO_ROUTE_PREFIX}/diagnostics`;

/**
 * Gli URL degli incontri.
 *
 * 🔴 L'argomento e' il `tournamentId` (il business id), **mai** `_id`. Tre rotte su cinque
 * rispondono **424** con un ObjectId — costato due PR (#122, e #123 perche' la prima era
 * meta': il link alla classifica aveva lo stesso difetto ed era sopravvissuto alla
 * correzione). Chi chiama passa `tournament.tournamentId || tournament.id`, come fa gia'
 * la scheda torneo.
 */
export const limuloMatchesUrl = (tournamentId: string): string =>
  `${limuloTournamentUrl(tournamentId)}/matches`;

export const limuloMatchUrl = (tournamentId: string, matchId: string): string =>
  `${limuloMatchesUrl(tournamentId)}/${encodeURIComponent(matchId)}`;

/** L'elenco dei propri profili di gioco. Nessun parametro: e' sempre «i miei». */
export const LIMULO_PROFILES_URL = `/${LIMULO_ROUTE_PREFIX}/profiles`;

/**
 * Il modulo per creare il profilo di gioco di UN gioco.
 *
 * ⚠️ Il segmento statico `new` sta PRIMA del parametro: `profiles/new/:gameId` non puo'
 * essere confusa con `profiles`, e non introduce un `:qualcosa` sotto `profiles/` che
 * domani ombreggerebbe una `profiles/:profileId`.
 *
 * `tournament` viaggia in query e non nel path perche' non identifica la schermata: e' il
 * posto DA CUI si e' arrivati, serve solo per tornarci, e un percorso che senza di esso non
 * si potesse aprire renderebbe il modulo irraggiungibile da ogni altro punto.
 * ⚠️ Il `gameId` e' il CODICE del gioco (`tournament.gameId`, passthrough kb#81), mai `_id`.
 */
export const limuloProfileCreateUrl = (gameId: string, tournamentId?: string): string => {
  const base = `/${LIMULO_ROUTE_PREFIX}/profiles/new/${encodeURIComponent(gameId)}`;
  return tournamentId ? `${base}?tournament=${encodeURIComponent(tournamentId)}` : base;
};

/**
 * Lo STESSO modulo, aperto su un profilo che esiste gia' — «ricollega», non «crea».
 *
 * ⚠️ **Il profilo viaggia in QUERY, non nel path, e non e' una preferenza di stile**: una
 * rotta `profiles/:profileId` introdurrebbe un `:qualcosa` sotto `profiles/` che ombreggia
 * `profiles/new/:gameId` — ed e' esattamente il difetto che il commento di
 * `LIMULO_ROUTE_PATHS` qui sopra esiste per impedire. Riusando `profiles/new/:gameId` la
 * tabella delle rotte non cambia affatto: cambia solo cosa il modulo trova in query.
 *
 * ⚠️ `gameId` resta il CODICE del gioco (kb#81), mai l'`_id`: tre rotte su cinque
 * rispondono `424` con un ObjectId.
 */
export const limuloProfileEditUrl = (gameId: string, profileId: string): string =>
  `/${LIMULO_ROUTE_PREFIX}/profiles/new/${encodeURIComponent(gameId)}?profile=${encodeURIComponent(profileId)}`;
